import React, { useEffect, useRef, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { composeStandalone } from '../lib/standaloneSuitCanvas';

const GARMENTS = { '1b_low': 'sb1', '2b': 'sb2', '3b': 'sb3', '2db': 'db2', '4db': 'db4', '6db': 'db6' };

export default function SuitDesignerPreview({ fabric, design, view }) {
  const front = view === 'closed' || view === 'back' ? 'closed' : 'open';
  const stem = `${GARMENTS[design.button_style] || 'sb2'}_${design.lapel_type || 'notch'}_${front}`;
  const box = useRef(null), canvas = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 }), [retry, setRetry] = useState(0);
  const [loading, setLoading] = useState(true), [error, setError] = useState('');

  useEffect(() => {
    let frame;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!box.current) return;
        const rect = box.current.getBoundingClientRect(), dpr = window.devicePixelRatio || 1;
        const scale = Math.min(rect.width / 928, rect.height / 1152) * dpr;
        const w = Math.round(928 * scale), h = Math.round(1152 * scale);
        setSize(previous => previous.w === w && previous.h === h ? previous : { w, h });
      });
    };
    const observer = new ResizeObserver(measure); observer.observe(box.current); measure();
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, []);

  useEffect(() => {
    if (size.w < 2 || size.h < 2) return;
    let live = true;
    setLoading(true); setError('');
    const start = performance.now();
    composeStandalone(stem, size.w, size.h, fabric, design).then(image => {
      if (!live || !canvas.current) return;
      const c = canvas.current;
      c.width = image.width; c.height = image.height;
      c.getContext('2d').drawImage(image, 0, 0);
      c.dataset.stem = stem;
      c.dataset.linerId = design.liner_id;
      c.dataset.buttonColour = design.button_colour_id;
      c.dataset.paintDurationMs = (performance.now() - start).toFixed(1);
      setLoading(false);
    }).catch(e => { if (live) { setError(e.message || 'Could not load jacket preview'); setLoading(false); } });
    return () => { live = false; };
  }, [stem, size, fabric, design, retry]);

  return (
    <div ref={box} className="relative w-full h-full flex items-center justify-center overflow-hidden bg-[#DEDCD6]" data-testid="designer-preview">
      <canvas ref={canvas} className="w-full h-full object-contain select-none" data-testid="designer-canvas" aria-label={`${front} front jacket preview`} />
      {loading && <div className="absolute bottom-4 right-4 bg-white/80 text-black p-2" role="status" data-testid="designer-loading"><Loader2 className="w-4 h-4 animate-spin" /><span className="sr-only">Loading jacket</span></div>}
      {error && <div className="absolute bottom-12 inset-x-4 bg-black/85 p-3 text-white text-sm" role="alert" data-testid="designer-preview-error">
        <p>{error}</p><button className="flex items-center gap-2 mt-2 text-[#C2A878] hover:text-white transition-colors" onClick={() => setRetry(n => n + 1)} data-testid="designer-preview-retry"><RefreshCw className="w-4 h-4" />Retry</button>
      </div>}
      <div className="absolute bottom-3 left-3 text-[10px] uppercase tracking-widest text-black/60 bg-white/60 px-2 py-1" data-testid="designer-view-caption">{front === 'open' ? 'Open · Front' : 'Closed · Front'}</div>
    </div>
  );
}